import { lockScroll, unlockScroll } from '@theme/utilities';

const SEEN_KEY = 'calvielle:welcome-popup:seen:v1';
const OPEN_DELAY = 1200;
const CLOSE_FALLBACK = 400;
const BLOCKING_LAYERS = [
  'dialog[open]', '[aria-modal="true"]', '[role="alertdialog"]',
  'theme-drawer[open]', '.menu-drawer-container[open]',
  '#shopify-pc__banner', '#shopify-pc__prefs',
  '[data-cookie-banner]', '[data-consent-banner]'
].join(',');

function isVisible(element) {
  if (!(element instanceof HTMLElement) || element.hidden || element.closest('[hidden], [aria-hidden="true"]')) return false;
  const style = getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
}

class CalvielleWelcomePopup extends HTMLElement {
  #dialog;
  #events;
  #observer;
  #openTimer;
  #closeTimer;
  #frame;
  #previousFocus;
  #phase = 'idle';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!(this.#dialog instanceof HTMLDialogElement) || typeof this.#dialog.showModal !== 'function') return;
    // This listener must survive pagehide cleanup for an unshown bfcache return.
    window.addEventListener('pageshow', this.#onPageShow);
    this.#initialize();
  }

  #initialize() {
    if (this.#events || window.Shopify?.designMode || this.#wasSeen()) return;
    this.#phase = 'idle';
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    this.#dialog.addEventListener('cancel', this.#onCancel, options);
    this.#dialog.addEventListener('click', this.#onClick, options);
    this.#dialog.addEventListener('close', this.#onClosed, options);
    this.#dialog.addEventListener('transitionend', this.#onTransitionEnd, options);
    document.addEventListener('visibilitychange', this.#schedule, options);
    document.addEventListener('focusout', this.#schedule, options);
    window.addEventListener('pagehide', this.#dispose, options);

    // Defer while an existing shopping, navigation or privacy surface needs attention.
    this.#observer = new MutationObserver(this.#schedule);
    this.#observer.observe(document.documentElement, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['open', 'hidden', 'aria-hidden', 'aria-modal', 'class', 'style']
    });
    this.#schedule();
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#dispose();
  }

  #onPageShow = (event) => {
    if (event.persisted && this.isConnected && this.#phase === 'closed') this.#initialize();
  };

  #wasSeen() {
    // This is a display preference only. Account eligibility stays on the server.
    try { return sessionStorage.getItem(SEEN_KEY) === '1'; }
    catch { return true; }
  }

  #hasBlockingLayer() {
    const root = document.documentElement;
    if (root.hasAttribute('scroll-lock') || root.classList.contains('cv-localization-open')) return true;
    for (const element of document.querySelectorAll(BLOCKING_LAYERS)) {
      if (!this.contains(element) && isVisible(element)) return true;
    }
    // The native Shop account surface may live in an open shadow root.
    for (const account of document.querySelectorAll('shopify-account')) {
      const layer = account.shadowRoot?.querySelector('dialog[open], [aria-modal="true"], [popover]:popover-open');
      if (layer && isVisible(layer)) return true;
    }
    const focused = document.activeElement;
    return focused instanceof HTMLElement && focused.matches('input, textarea, select, [contenteditable="true"]');
  }

  #schedule = () => {
    if (this.#phase !== 'idle' || this.#openTimer || !this.isConnected || document.hidden) return;
    this.#openTimer = window.setTimeout(this.#attemptOpen, OPEN_DELAY);
  };

  #attemptOpen = () => {
    this.#openTimer = undefined;
    if (this.#phase !== 'idle' || !this.isConnected || document.hidden) return;
    if (this.#wasSeen()) { this.#observer?.disconnect(); return; }
    if (this.#hasBlockingLayer()) return;

    // Persist before opening so returning from account verification cannot repeat the invitation.
    try { sessionStorage.setItem(SEEN_KEY, '1'); }
    catch { this.#observer?.disconnect(); return; }
    this.#observer?.disconnect();
    this.#previousFocus = document.activeElement;
    this.#phase = 'opening';
    try { this.#dialog.showModal(); }
    catch { this.#phase = 'closed'; return; }
    lockScroll(this.#dialog);
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#reveal();
    } else {
      this.#frame = requestAnimationFrame(() => {
        this.#frame = requestAnimationFrame(this.#reveal);
      });
    }
  };

  #reveal = () => {
    this.#frame = undefined;
    if (this.#phase !== 'opening' || !this.#dialog.open || !this.isConnected) return;
    this.#dialog.classList.add('is-visible');
    this.#phase = 'open';
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.#dismiss();
  };

  #onClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-cv-welcome-close]')) {
      this.#dismiss();
      return;
    }
    if (event.target !== this.#dialog) return;
    const bounds = this.#dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.#dismiss();
  };

  #dismiss() {
    if (!this.#dialog.open || this.#phase === 'closing') return;
    cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    const wasVisible = this.#dialog.classList.contains('is-visible');
    this.#phase = 'closing';
    this.#dialog.classList.remove('is-visible');
    if (!wasVisible || matchMedia('(prefers-reduced-motion: reduce)').matches) this.#finishClose();
    else this.#closeTimer = window.setTimeout(this.#finishClose, CLOSE_FALLBACK);
  }

  #onTransitionEnd = (event) => {
    if (event.target === this.#dialog && event.propertyName === 'opacity' && this.#phase === 'closing') this.#finishClose();
  };

  #finishClose = () => {
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    if (this.#dialog?.open) this.#dialog.close();
    this.#onClosed();
  };

  #onClosed = () => {
    if (this.#phase === 'closed') return;
    this.#phase = 'closed';
    cancelAnimationFrame(this.#frame);
    window.clearTimeout(this.#closeTimer);
    this.#dialog.classList.remove('is-visible');
    unlockScroll(this.#dialog);
    if (this.#previousFocus instanceof HTMLElement && this.#previousFocus.isConnected && !this.#hasBlockingLayer()) {
      this.#previousFocus.focus({ preventScroll: true });
    }
  };

  #dispose = () => {
    this.#phase = 'closed';
    this.#observer?.disconnect();
    this.#events?.abort();
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
    cancelAnimationFrame(this.#frame);
    this.#observer = undefined;
    this.#events = undefined;
    this.#openTimer = undefined;
    this.#closeTimer = undefined;
    this.#frame = undefined;
    this.#previousFocus = undefined;
    if (this.#dialog?.open) this.#dialog.close();
    if (this.#dialog) unlockScroll(this.#dialog);
  };
}

if (!customElements.get('calvielle-welcome-popup')) customElements.define('calvielle-welcome-popup', CalvielleWelcomePopup);
